import React, { useMemo, useState } from 'react';
import { Table, Button, Popconfirm, Space, Modal, Input, Tag } from 'antd';
import { useProduct } from '../../store/productContext';
import ProductForm from './ProductForm';
import { Product, StockEntry } from '../../api/productApi';
import { useTranslation } from 'react-i18next';
import PageNavigation from '../Layout/PageNavigation';

const ProductList: React.FC = () => {
    const { t } = useTranslation();
    const { products, loading, createProduct, updateProduct, deleteProduct } = useProduct();
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filteredProducts = useMemo(() => {
        const q = search.toLowerCase();
        return products.filter(p =>
            (p.name ?? '').toLowerCase().includes(q) ||
            (p.code ?? '').toLowerCase().includes(q) ||
            (p.category ?? '').toLowerCase().includes(q) ||
            (p.sku ?? '').toLowerCase().includes(q) ||
            (p.barcode ?? '').toLowerCase().includes(q)
        );
    }, [products, search]);

    const handleEdit = (record: Product) => {
        setEditingProduct(record);
        setOpen(true);
    };

    const handleCreate = () => {
        setEditingProduct(null);
        setOpen(true);
    };

    const handleSubmit = async (values: Product) => {
        if (editingProduct && editingProduct.id) {
            await updateProduct(editingProduct.id, values);
        } else {
            await createProduct(values);
        }
        setOpen(false);
    };

    const columns = [
        { title: t('code'), dataIndex: 'code', key: 'code' },
        { title: t('name'), dataIndex: 'name', key: 'name' },
        { title: t('category'), dataIndex: 'category', key: 'category' },
        { title: t('sku'), dataIndex: 'sku', key: 'sku' },
        { title: t('barcode'), dataIndex: 'barcode', key: 'barcode' },
        {
            title: t('buy_price_dollar'), dataIndex: 'buyPriceInDollar', key: 'buyPriceInDollar',
            render: (val: number) => val?.toLocaleString() || '-'
        },
        {
            title: t('buy_price_iqd'), dataIndex: 'buyPriceInDinar', key: 'buyPriceInDinar',
            render: (val: number) => val?.toLocaleString() || '-'
        },
        {
            title: t('sell_price_dollar'), dataIndex: 'sellPriceInDollar', key: 'sellPriceInDollar',
            render: (val: number) => val?.toLocaleString() || '-'
        },
        {
            title: t('sell_price_iqd'), dataIndex: 'sellPriceInDinar', key: 'sellPriceInDinar',
            render: (val: number) => val?.toLocaleString() || '-'
        },
        {
            title: t('stock_entries'),
            key: 'stockEntries',
            render: (_: any, rec: Product) => {
                const entries = rec.currentStock || [];
                const unitTotals: Record<string, number> = {};
                entries.forEach((s: StockEntry) => {
                    unitTotals[s.unit] = (unitTotals[s.unit] || 0) + s.qty;
                });
                return (
                    <div>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {entries.map((s: StockEntry, idx: number) => (
                                <li key={idx}>
                                    <b>{t('unit')}:</b> {s.unit}
                                    {" | "}
                                    <b>{t('qty')}:</b> {s.qty}
                                    {s.unitLength ? <>{" | "}<b>{t('unit_length')}:</b> {s.unitLength}</> : null}
                                    {s.date ? <>{" | "}<b>{t('date')}:</b> {new Date(s.date).toLocaleDateString()}</> : null}
                                </li>
                            ))}
                        </ul>
                        {entries.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                                {Object.entries(unitTotals).map(([unit, qty]) => (
                                    <Tag key={unit} color="blue" style={{ marginBottom: 2 }}>
                                        {unit}: {qty}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: t('total_stock'),
            key: 'totalStock',
            render: (_: any, rec: Product) => {
                const total = (rec.currentStock || []).reduce((sum: number, s: StockEntry) => sum + s.qty, 0);
                return <Tag color={total > 0 ? 'green' : 'red'}>{total}</Tag>;
            }
        },
        {
            title: t('actions'), key: 'actions',
            render: (_: any, record: Product) => (
                <Space>
                    <Button type="primary" onClick={() => handleEdit(record)}>{t('edit')}</Button>
                    <Popconfirm
                        title={t('are_you_sure')}
                        onConfirm={() => deleteProduct(record.id as string)}
                    >
                        <Button type="primary" danger>{t('delete')}</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <>
            <PageNavigation title={t('products') as string} />
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={handleCreate}>
                    {t('add_product')}
                </Button>
                <Input.Search
                    allowClear
                    placeholder={t('search_products') as string}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ maxWidth: 300 }}
                />
            </Space>
            <Table
                rowKey={record => record.id as string}
                columns={columns}
                dataSource={filteredProducts}
                loading={loading}
                scroll={{ x: true }}
            />
            <Modal
                title={editingProduct ? t('edit_product') : t('add_product')}
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                destroyOnClose
                width="90vw"
            >
                <ProductForm
                    initialValues={editingProduct ?? undefined}
                    onFinish={handleSubmit}
                    loading={loading}
                />
            </Modal>
        </>
    );
};

export default ProductList;